/**
 * Exit-intent consultation modal.
 *
 * Fires when the visitor moves toward the browser chrome (desktop) or when
 * the tab visibility changes to hidden (mobile fallback). One-shot per
 * session, stores dismissal in localStorage so returning users aren't
 * spammed.
 *
 * Compliance-conscious: no guarantees, no efficacy claims, no urgency
 * copy ("last chance!"). Uses the visit-type-agnostic "meet with a
 * licensed medical provider" language.
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, ShieldCheck } from "lucide-react";
import { ScheduleConsultationCTA } from "./ScheduleConsultationCTA";
import { trackEvent } from "../lib/analytics";

const STORAGE_KEY = "vv_exit_intent_shown_v1";
const COOLDOWN_HOURS = 24 * 3; // don't re-show within 3 days
const HIDDEN_PATH_PREFIXES = ["/admin", "/consultation", "/thank-you"];

export function ExitIntentModal() {
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Skip on admin / consultation / thank-you routes
    if (HIDDEN_PATH_PREFIXES.some((p) => loc.pathname.startsWith(p))) return;

    // Skip if we've shown it recently
    try {
      const ts = parseInt(window.localStorage.getItem(STORAGE_KEY) || "0", 10);
      if (Date.now() - ts < COOLDOWN_HOURS * 60 * 60 * 1000) return;
    } catch (_) { /* localStorage blocked — proceed */ }

    let armed = false;
    // Give the visitor at least 20s before we're eligible to fire
    const armTimer = window.setTimeout(() => { armed = true; }, 20 * 1000);

    const trigger = (reason) => {
      if (!armed) return;
      armed = false;
      try { window.localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch (_) {}
      try { trackEvent("exit_intent_shown", { reason }); } catch (_) {}
      setOpen(true);
    };

    // Desktop: cursor leaves viewport toward top edge
    const onMouseOut = (e) => {
      if (!e.relatedTarget && e.clientY <= 0) trigger("mouseout-top");
    };
    // Mobile fallback: page visibility change
    const onVis = () => {
      if (document.visibilityState === "hidden") trigger("visibility-hidden");
    };

    document.addEventListener("mouseout", onMouseOut);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.clearTimeout(armTimer);
      document.removeEventListener("mouseout", onMouseOut);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [loc.pathname]);

  const dismiss = () => {
    try { trackEvent("exit_intent_dismissed"); } catch (_) {}
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      data-testid="exit-intent-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={dismiss}
    >
      <div
        className="w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 md:p-8">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[11px] uppercase tracking-widest text-[color:var(--vv-teal)] font-semibold flex items-center gap-2">
              <ShieldCheck size={14} /> Before you go
            </div>
            <button
              onClick={dismiss}
              aria-label="Close"
              className="text-stone-400 hover:text-stone-800"
              data-testid="exit-intent-close"
            >
              <X size={20} />
            </button>
          </div>
          <h3 className="font-display text-2xl md:text-3xl leading-tight text-[color:var(--vv-ink)] mb-3">
            Interested in peptide therapy?
          </h3>
          <p className="text-stone-700 leading-relaxed mb-5">
            Meet with one of our licensed medical providers to determine
            whether peptide therapy is appropriate for you. If clinically
            appropriate, we'll create a personalized treatment plan.
          </p>
          <div className="flex items-center gap-2">
            <ScheduleConsultationCTA
              location="exit-intent-modal"
              size="lg"
              fullWidth
            >
              Schedule My Consultation
            </ScheduleConsultationCTA>
          </div>
          <p className="text-xs text-stone-500 mt-4 text-center">
            Information on this site is educational and does not replace
            personalized medical advice. Eligibility depends on medical history,
            goals, and provider assessment.
          </p>
        </div>
      </div>
    </div>
  );
}

export default ExitIntentModal;
